import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useDispatch } from "react-redux";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import InputField from "../forms/input-field";
import InputPhoneField from "../forms/phone-input-field";
import SelectField from "../forms/select-field";
import TextAreaField from "../forms/textarea-field";
import LoadingOverlay from "../helper-components/overlay-loading";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import { customerTypeoptions } from "../helper-components/data";
import { adminCustomerCreateAction } from "../../redux/actions/admin-customer-action";
import { gstSearchTaxpayerAction } from "../../redux/actions/general-action";

// Same general-details payload the Create Party page (admin-customer/general.js)
// sends, so the backend gets every field it expects.
const defaultGeneral = {
  cmr_first_name: "",
  cmr_last_name: "",
  cmr_type: "individual",
  cmr_dob: "",
  cmr_phone_number: "",
  cmr_email: "",
  cmr_active_status: true,
  cmr_active_from: null,
  cmr_inactive_from: null,
  cmr_remarks: "",
  cmr_acquired_source: "",
  cmr_balance: 0,
  cmr_currency: null,
  cmr_payment_terms: "",
  cmr_shipping_method: "",
  is_ecommerce_cmr: false,
  is_vendor_cmr: false,
  is_corporate_cmr: false,
  is_store_cmr: false,
  update_sign: null,
  cmr_country: "",
  cmr_state: "",
  cmr_district: "",
  cmr_city: "",
  cmr_town: "",
  cmr_village: "",
  cmr_street1: "",
  cmr_street2: "",
  cmr_pincode: "",
  cmr_address: "",
  cmr_company: "",
  cmr_pan: "",
  cmr_sector: "",
  cmr_tax: "",
  cmr_business_type: "",
  cmr_gstin: "",
  cmr_group_id: "",
  inter_company_sync: false,
  cmr_tcs: null,
  cmr_tds: null,
  store_id: "",
  cmr_fssai: "",
  cmr_dl_no: "",
  cmr_gstin_certificate: "",
  cmr_dl_certificate: "",
  cmr_fssai_certificate: "",
  cmr_latitude: "",
  cmr_longitude: "",
  cmr_party_category: "",
};

const phoneRegex = /^[0-9]\d{9}$/;
const emailRegex =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const pincodeRegex = /^[1-9][0-9]{5}$/;
const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// GSTIN / PAN are business-only and optional (as in Party Management), but
// must be valid when entered
const optionalFormat = (regex, message) =>
  Yup.string()
    .nullable()
    .test("format", message, function (value) {
      if (this.parent.cmr_type !== "business") return true;
      return !value || value.trim() === ""
        ? true
        : regex.test(value.toUpperCase());
    });

const validationSchema = Yup.object().shape({
  cmr_first_name: Yup.string()
    .trim()
    .required("Full Name is required")
    .min(2, "Full Name must be at least 2 characters")
    .max(100, "Full Name must not exceed 100 characters"),
  cmr_phone_number: Yup.string()
    .required("Phone is required")
    .matches(phoneRegex, "Please enter a valid 10-digit phone number"),
  cmr_email: Yup.string()
    .matches(emailRegex, {
      message: "Please enter a valid email address",
      excludeEmptyString: true,
    })
    .nullable(),
  cmr_pincode: Yup.string().matches(
    pincodeRegex,
    "Please enter a valid 6-digit pincode"
  ),
  cmr_address: Yup.string().max(250, "Address must not exceed 250 characters"),
  cmr_age: Yup.number()
    .transform((value, original) => (original === "" ? undefined : value))
    .integer("Age must be a whole number")
    .min(0, "Age cannot be negative")
    .max(120, "Please enter a valid age"),
  cmr_dob: Yup.string().test(
    "not-future",
    "Date of Birth cannot be in the future",
    (value) => !value || !moment(value).isAfter(moment(), "day")
  ),
  cmr_gstin: optionalFormat(
    gstinRegex,
    "Please enter a valid GST number (15 digits)"
  ),
  cmr_pan: optionalFormat(
    panRegex,
    "Please enter a valid PAN number (e.g., ABCDE1234F)"
  ),
});

const labelClass =
  "text-[var(--alert-management-label-color)] text-[length:var(--alert-management-label-fs)] font-[var(--alert-management-sub-title-fw)] w-full";
const inputClass =
  "min-h-[42px] rounded-[8px] px-2 bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] font-[var(--alert-management-input-fw)] rounded placeholder:text-[var(--input-place-color)] placeholder:text-[length:var(--input-place-font-size)] placeholder:font-[var(--input-place-font-weight)] focus:outline-none focus:text-[var(--input-color)]";

// Same "Business Associations" flags as Party Management (admin-customer/general.js)
const businessAssociations = [
  { name: "is_ecommerce_cmr", label: "E-commerce" },
  { name: "is_vendor_cmr", label: "Vendor" },
  { name: "is_corporate_cmr", label: "Corporate" },
  { name: "is_store_cmr", label: "Store" },
  { name: "inter_company_sync", label: "Inter Company Vendor" },
];

// Quick "+ New Customer" / "+ New Vendor" from a document screen (billing,
// purchase invoice): creates the party without leaving the page, then hands
// the created party back via onCreated.
export default function NewPartyPanel({
  open,
  setOpen,
  searchText = "",
  onCreated = () => {},
  partyLabel = "Customer",
  defaultType = "individual",
  // e.g. { is_vendor_cmr: true } to pre-tick Vendor from purchase screens
  defaultAssociations = {},
}) {
  const [loading, setLoading] = useState(false);
  const [gstinSearchLoading, setGstinSearchLoading] = useState(false);
  const dispatch = useDispatch();

  // Same GSTIN lookup as Party Management: fills name, pincode and address
  const handleGstinSearch = (gstinValue, setFieldValue) => {
    const value = (gstinValue || "").trim().toUpperCase();
    if (!gstinRegex.test(value) || gstinSearchLoading) return;
    setGstinSearchLoading(true);
    dispatch(
      gstSearchTaxpayerAction(
        value,
        (res) => {
          setGstinSearchLoading(false);
          const data = res?.data;
          if (!data) {
            errorToast(res?.message || "No GSTIN details found");
            return;
          }
          setFieldValue("cmr_first_name", data?.lgnm || "");
          setFieldValue("cmr_gstin", data?.gstin || value);
          // PAN is characters 3-12 of a GSTIN
          setFieldValue("cmr_pan", (data?.gstin || value).slice(2, 12));
          const addr = data?.pradr?.addr;
          if (addr?.pncd) setFieldValue("cmr_pincode", addr.pncd);
          if (addr) {
            const addressLine = [addr.flno, addr.bno, addr.bnm, addr.st, addr.loc]
              .filter((part) => part && String(part).trim())
              .join(", ")
              .slice(0, 250);
            setFieldValue("cmr_address", addressLine);
          }
          successToast(res?.message || "GSTIN details fetched successfully");
        },
        (err) => {
          setGstinSearchLoading(false);
          errorToast(
            err?.data?.message || err?.message || "Failed to fetch GSTIN details"
          );
        }
      )
    );
  };
  const today = new Date().toISOString().split("T")[0];

  // Typed digits prefill the phone, typed letters prefill the name
  const typed = (searchText ?? "").toString().trim();
  const typedIsNumber = /^\d+$/.test(typed);
  const initialValues = {
    cmr_first_name: typedIsNumber ? "" : typed,
    cmr_phone_number: typedIsNumber ? typed.slice(0, 10) : "",
    cmr_type: defaultType,
    cmr_email: "",
    cmr_dob: "",
    cmr_age: "",
    ...Object.fromEntries(
      businessAssociations.map(({ name }) => [
        name,
        !!defaultAssociations[name],
      ])
    ),
    cmr_pincode: "",
    cmr_address: "",
    cmr_gstin: "",
    cmr_pan: "",
  };

  const onSave = (values) => {
    if (loading) return;
    setLoading(true);
    const payload = {
      ...defaultGeneral,
      ...values,
      cmr_first_name: values.cmr_first_name.trim(),
      cmr_phone_number: String(values.cmr_phone_number),
    };
    if (values.cmr_type === "business") {
      payload.cmr_gstin = (values.cmr_gstin || "").trim().toUpperCase();
      payload.cmr_pan = (values.cmr_pan || "").trim().toUpperCase();
      // DOB is only shown for individuals
      delete payload.cmr_dob;
    } else {
      // Business-only fields are hidden for individuals, don't send leftovers
      payload.cmr_gstin = "";
      payload.cmr_pan = "";
    }
    if (!payload.cmr_dob) delete payload.cmr_dob;
    // Age is saved on its own (cmr_age); it's either typed or calculated from DOB
    if (values.cmr_type === "individual" && values.cmr_age !== "") {
      payload.cmr_age = parseInt(values.cmr_age, 10);
    } else {
      delete payload.cmr_age;
    }
    dispatch(
      adminCustomerCreateAction(
        payload,
        (data) => {
          setLoading(false);
          successToast(data?.message || `${partyLabel} created`);
          onCreated({ ...payload, ...data?.data?.general });
          setOpen(false);
        },
        (data) => {
          setLoading(false);
          errorToast(
            data?.data?.message ||
              data?.message ||
              `Could not create ${partyLabel.toLowerCase()}`
          );
        }
      )
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (!loading) setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full w-[400px] sm:w-[560px] bg-white flex flex-col rounded-tl-lg rounded-bl-lg shadow-xl">
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={onSave}
                  enableReinitialize
                >
                  {({ values, submitForm, setFieldValue }) => (
                    // No <form> here: this panel is portalled out of the
                    // billing <form>, and a submit would bubble up to it
                    <>
                      {/* Header */}
                      <div
                        className="h-[61px] shrink-0 bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b"
                        style={{ borderColor: "var(--slide-panel-border)" }}
                      >
                        <Dialog.Title className="text-[var(--slide-panel-h-fg)] text-[length:var(--slide-panel-h-fs)] font-[var(--slide-panel-h-fw)]">
                          New {partyLabel}
                        </Dialog.Title>
                        <AddButton
                          onclkFn={() => setOpen(false)}
                          icon={
                            <CloseIcon
                              width="12px"
                              height="12px"
                              fill="var(--close-button-fg)"
                            />
                          }
                          text={
                            <span className="flex items-center gap-1">
                              Close <span className="opacity-50">(Esc)</span>
                            </span>
                          }
                          className="flex h-[32px] items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                        />
                      </div>

                      {/* Body */}
                      <div
                        className="flex-1 p-4 overflow-y-auto"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                            e.preventDefault();
                            submitForm();
                          }
                        }}
                      >
                        <div className="flex flex-col gap-4 border border-[var(--slide-panel-border)] border-radius p-4">
                          <InputPhoneField
                            isRequired={true}
                            autoFocus={!initialValues.cmr_phone_number}
                            labelClass={labelClass}
                            parentClass="flex flex-col gap-2 w-full"
                            name="cmr_phone_number"
                            placeholder="Enter Phone Number"
                            label="Phone Number"
                            defaultInputClass={`${inputClass} rounded-l-none rounded-r-[8px]`}
                            containerClass="w-full h-min"
                          />
                          <InputField
                            allowSpecial={true}
                            isRequired={true}
                            autoFocus={!!initialValues.cmr_phone_number}
                            labelClass={labelClass}
                            parentClass="flex flex-col gap-2 w-full"
                            name="cmr_first_name"
                            placeholder="Enter Full Name"
                            label="Full Name"
                            defaultInputClass={inputClass}
                            containerClass="w-full h-min"
                          />
                          <SelectField
                            defaultValue={customerTypeoptions?.find(
                              (option) => option?.value === values?.cmr_type
                            )}
                            label="Party Type"
                            labelClass={labelClass}
                            general_color="true"
                            borderclass="border border-[var(--pay-met-input-border)] rounded-lg"
                            isContainer={true}
                            containerClass="w-full flex flex-col gap-2"
                            parentClass="w-full flex flex-col gap-2"
                            id="cmr_type"
                            placeholder="Select Party Type"
                            options={customerTypeoptions}
                            name="cmr_type"
                            isSearchable={true}
                          />
                          {values.cmr_type === "business" && (
                            <>
                              <div className="flex gap-2">
                                <div className="flex-1 min-w-0">
                                  <InputField
                                    maxLength={15}
                                    labelClass={labelClass}
                                    parentClass="flex flex-col gap-2 w-full"
                                    name="cmr_gstin"
                                    placeholder="Enter GSTIN"
                                    label="GSTIN"
                                    defaultInputClass={inputClass}
                                    containerClass="w-full h-min"
                                  />
                                </div>
                                <div className="flex flex-col gap-2">
                                  {/* invisible spacer so the button lines up with the input, not the label */}
                                  <span
                                    aria-hidden="true"
                                    className="invisible text-[length:var(--alert-management-label-fs)] font-[var(--alert-management-sub-title-fw)]"
                                  >
                                    Search
                                  </span>
                                  <AddButton
                                    type="button"
                                    prefixIcon={false}
                                    text={<span>Search</span>}
                                    loading={gstinSearchLoading}
                                    isDisabled={
                                      !gstinRegex.test(
                                        (values?.cmr_gstin || "").toUpperCase()
                                      )
                                    }
                                    onclkFn={() =>
                                      handleGstinSearch(
                                        values?.cmr_gstin,
                                        setFieldValue
                                      )
                                    }
                                    className="min-h-[42px] px-4 flex items-center justify-center whitespace-nowrap rounded-[8px] gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)] disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:hover:bg-gray-100"
                                  />
                                </div>
                              </div>
                              <InputField
                                type="text"
                                maxLength={10}
                                labelClass={labelClass}
                                parentClass="flex flex-col gap-2 w-full"
                                name="cmr_pan"
                                placeholder="Enter PAN"
                                label="PAN"
                                defaultInputClass={inputClass}
                                containerClass="w-full h-min"
                              />
                            </>
                          )}
                          <InputField
                            allowSpecial={true}
                            type="email"
                            onKeyDown={() => {}}
                            labelClass={labelClass}
                            parentClass="flex flex-col gap-2 w-full"
                            name="cmr_email"
                            placeholder="Enter Email"
                            label="Email"
                            defaultInputClass={inputClass}
                            containerClass="w-full h-min"
                          />
                          {values.cmr_type === "individual" && (
                            <>
                              <InputField
                                type="date"
                                max={today}
                                labelClass={labelClass}
                                parentClass="flex flex-col gap-2 w-full"
                                name="cmr_dob"
                                placeholder="Enter Date of Birth"
                                label="Date of Birth"
                                defaultInputClass={inputClass}
                                containerClass="w-full h-min"
                                handleChange={(e) => {
                                  // With a DOB, Age is calculated and locked;
                                  // clearing the DOB frees Age for manual entry
                                  const dob = e.target.value;
                                  setFieldValue(
                                    "cmr_age",
                                    dob && moment(dob).isValid()
                                      ? moment().diff(moment(dob), "years")
                                      : ""
                                  );
                                }}
                              />
                              {/* Enter either DOB or Age. Age never fills DOB. */}
                              <InputField
                                type="number"
                                maxLength={3}
                                maxValue={120}
                                isDisabled={!!values.cmr_dob}
                                labelClass={labelClass}
                                parentClass="flex flex-col gap-2 w-full"
                                name="cmr_age"
                                placeholder="Enter Age (in years)"
                                label="Age"
                                defaultInputClass={`${inputClass} disabled:cursor-not-allowed disabled:opacity-70`}
                                containerClass="w-full h-min"
                              />
                            </>
                          )}
                          <InputField
                            labelClass={labelClass}
                            parentClass="flex flex-col gap-2 w-full"
                            name="cmr_pincode"
                            placeholder="Enter Pincode"
                            label="Pincode"
                            maxLength={6}
                            defaultInputClass={inputClass}
                            containerClass="w-full h-min"
                          />
                          <div className="flex flex-col gap-2 w-full">
                            <label className={labelClass}>
                              Address (max 250 Characters)
                            </label>
                            <TextAreaField
                              maxLength={250}
                              name="cmr_address"
                              placeholder="Enter Address"
                              labelClass={labelClass}
                              parentClass="flex flex-col gap-2 w-full"
                              label=""
                              defaultInputClass={inputClass}
                              containerClass="flex flex-col gap-2 w-full"
                            />
                          </div>
                        </div>

                        <section className="p-4 mt-4 bg-[var(--customer-business-bg)] border border-[var(--customer-business-border)] rounded-lg">
                          <label className="text-[var(--alert-management-label-color)] text-[length:var(--customer-current-nav-fs)] font-[var(--alert-management-sub-title-fw)] w-full">
                            Business Associations
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3">
                            {businessAssociations.map(({ name, label }) => (
                              <label
                                key={name}
                                className="flex flex-row w-full items-center gap-2 py-3 rounded-lg bg-[var(--alert-management-variable-bg)] cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={!!values[name]}
                                  onChange={(e) =>
                                    setFieldValue(name, e.target.checked)
                                  }
                                  className="custom-checkbox form-check-input ml-[3px]"
                                />
                                <span className="text-[length:var(--alert-management-label-fs)] font-[var(--alert-management-variable-fw)]">
                                  {label}
                                </span>
                              </label>
                            ))}
                          </div>
                        </section>
                      </div>

                      {/* Footer */}
                      <div
                        className="shrink-0 flex justify-between px-4 py-3 border-t bg-white"
                        style={{ borderColor: "var(--slide-panel-border)" }}
                      >
                        <AddButton
                          prefixIcon={false}
                          onclkFn={() => setOpen(false)}
                          text={<span>Cancel</span>}
                          className="flex items-center px-3 py-2 whitespace-nowrap rounded-md gap-[8px] cursor-pointer text-[var(--primary-button-bg-color)] border border-[var(--primary-button-bg-color)] text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]"
                          type="button"
                        />
                        <AddButton
                          prefixIcon={false}
                          onclkFn={submitForm}
                          text={<span>Save {partyLabel}</span>}
                          className="flex items-center px-3 py-2 whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-create-fs)] font-[var(--primary-button-font-weight)]"
                          type="button"
                        />
                      </div>
                    </>
                  )}
                </Formik>
                <LoadingOverlay isLoading={loading} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
